import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, TrendingUp, Shield, Smartphone, Euro, Globe, Coins, BarChart3 } from 'lucide-react';
import { AnimatedCard } from '@/components/AnimatedCard';
import { formatFees, formatDeposit } from '@/utils/formatCurrency';

// Helper to check if logo is a valid image URL
const isImageUrl = (logo) => {
  if (!logo) return false;
  return logo.startsWith('http') || logo.startsWith('/api') || logo.startsWith('data:');
};

// Transform logo URL to use current server
const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (!isImageUrl(logo)) return null;
  
  // Extract filename from any URL and use current backend
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // If it's already a relative path, prepend API URL
  if (logo.startsWith('/api')) {
    return `${API_URL}${logo}`;
  }
  
  // Extract filename from full URLs and rebuild with current domain
  const match = logo.match(/\/uploads\/logos\/([^/?#]+)/);
  if (match) {
    return `${API_URL}/api/uploads/logos/${match[1]}`;
  }
  
  // For external URLs, return as-is
  if (logo.startsWith('http')) {
    return logo;
  }
  
  return null;
};

export const BrokerCard = ({ broker, variant = 'default' }) => {
  const navigate = useNavigate();
  const logoUrl = getLogoUrl(broker.logo);
  const [imageError, setImageError] = useState(false);
  
  // Generate slug from broker name (same as backend)
  const brokerSlug = broker.slug || broker.name.toLowerCase().replace(/[^a-z0-9]+/g, '');
  
  // Collect active features for display
  const activeFeatures = [];
  if (broker.cryptoSupport) activeFeatures.push({ icon: Coins, label: 'Crypto' });
  if (broker.mobileApp) activeFeatures.push({ icon: Smartphone, label: 'Mobile' });
  if (broker.webPlatform) activeFeatures.push({ icon: Globe, label: 'Web' });
  if (broker.optionsTrading) activeFeatures.push({ icon: BarChart3, label: 'Options' });
  if (broker.forexTrading) activeFeatures.push({ icon: TrendingUp, label: 'Forex' });
  
  const handleReviewClick = (e) => {
    e.stopPropagation();
    navigate(`/broker/${brokerSlug}#reviews`);
  };
  
  return (
    <div 
      className="w-full cursor-pointer"
      onClick={() => navigate(`/broker/${brokerSlug}`)}
      data-testid={`broker-card-${broker.id}`}
    >
      <AnimatedCard className="h-full hover:-translate-y-2 transition-transform duration-300">
        <div className="p-6 space-y-4">
          {/* Header with Large Logo */}
          <div className="flex flex-col items-center text-center space-y-3">
            {/* Logo - ausgefüllt wie auf neobroker.com */}
            <div className="w-20 h-20 rounded-2xl bg-white dark:bg-gray-800 flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700">
              {logoUrl && !imageError ? (
                <img 
                  src={logoUrl} 
                  alt={broker.name} 
                  width="80"
                  height="80"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  onError={() => setImageError(true)}
                />
              ) : (
                <span className="flex items-center justify-center text-4xl">
                  {broker.logo && !isImageUrl(broker.logo) ? broker.logo : '🏦'}
                </span>
              )}
            </div>
            
            {/* Name and Tagline */}
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-2">
                <h3 className="text-lg font-semibold text-foreground">{broker.name}</h3>
                {broker.promos && broker.promos.length > 0 && (
                  <Badge variant="secondary" className="bg-gradient-to-r from-[hsl(38,92%,50%)] to-[hsl(28,92%,50%)] text-white border-0 text-xs">
                    Promo
                  </Badge>
                )}
              </div>
              <p className="text-sm text-foreground/70">{broker.tagline}</p>
            </div>
          </div>
          
          {/* Rating - Clickable, centered, tighter spacing */}
          <div 
            className="flex items-center justify-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleReviewClick}
          >
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 flex-shrink-0 ${
                    i < Math.floor(broker.rating)
                      ? 'fill-[hsl(38,92%,50%)] text-[hsl(38,92%,50%)]'
                      : 'text-muted'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm font-medium text-foreground">{broker.rating}</span>
            <span className="text-sm text-foreground/70 hover:text-[hsl(210,100%,56%)] hover:underline">({broker.reviewCount})</span>
          </div>
          
          {/* Key Info - Centered */}
          <div className="flex justify-center gap-6 py-2">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-1 text-[hsl(210,100%,56%)]">
                <Euro className="w-4 h-4" />
                <span className="text-xs text-foreground/70">Min. Deposit</span>
              </div>
              <p className="font-medium text-foreground">{formatDeposit(broker.minDeposit)}</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-1 text-[hsl(210,100%,56%)]">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs text-foreground/70">Fees</span>
              </div>
              <p className="font-medium text-foreground">{formatFees(broker.tradingFees) || 'Variable'}</p>
            </div>
          </div>
          
          {/* Capabilities Badges */}
          {broker.specialCapabilities && broker.specialCapabilities.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center">
              {broker.specialCapabilities.slice(0, 4).map((capability, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {capability}
                </Badge>
              ))}
              {broker.specialCapabilities.length > 4 && (
                <Badge variant="outline" className="text-xs">
                  +{broker.specialCapabilities.length - 4}
                </Badge>
              )}
            </div>
          )}
          
          {/* Feature Icons */}
          <div className="flex items-center justify-center space-x-4 pt-2 border-t border-white/20 dark:border-white/10">
            {activeFeatures.slice(0, 4).map((feature, index) => (
              <div key={index} className="flex items-center space-x-1 text-xs text-foreground/70">
                <feature.icon className="w-3.5 h-3.5" />
                <span>{feature.label}</span>
              </div>
            ))}
          </div>
          
          {/* CTA */}
          <div className="pt-2">
            <Link to={`/broker/${brokerSlug}`} onClick={(e) => e.stopPropagation()}>
              <Button 
                className="w-full bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105"
              >
                View Details
              </Button>
            </Link>
          </div>
        </div>
      </AnimatedCard>
    </div>
  );
};
